import moment from "moment-timezone";
import { Badge } from "@/components/ui/badge";
import { CommonDestinationTimezone } from "@/lib/api/utils";
import {
  ContractTypeOptions,
  CommodityOptions,
  findMatchOption,
  OtcContractLayerDto,
} from "@/lib/api/otc";
import {
  OTC_LAYER_FIELD_REGISTRY,
  OtcLayerFieldDef,
  getOtcLayerFieldDef,
} from "@/section/otc/contract-form-v3/fieldRegistry";
import { formatContractReferenceDisplay } from "@/section/otc/contract-form-v2/contract-reference-utils";
import { generateAmendmentTitleForLayer } from "./common";
import { safeParseDecimal } from "@/lib/api/common";

/**
 * Registry fields whose stored value is an id/code but which have a
 * human-readable label denormalized onto the layer `display_snapshot`. The
 * read-only card prefers the display label over the raw id.
 */
const DISPLAY_SNAPSHOT_LABEL_KEYS: Record<string, string> = {
  contract_type: "contract_type_label",
  commodity: "commodity_label",
  customer_id: "customer_name",
  trader_id: "trader_name",
  account_no_a: "account_no_a_label",
  account_no_b: "account_no_b_label",
};

/**
 * Derived fields that the backend computes and stores on the contract.
 * These are NOT in the field registry (they are not editable), but their
 * values are present in `display_snapshot` and should be surfaced in the
 * read-only card so users can see how the math works out.
 */
const DERIVED_DISPLAY_FIELDS: ReadonlyArray<{ key: string; label: string }> = [
  { key: "margin", label: "Lighthouse Fee" },
  { key: "margin_amount", label: "Margin Amount" },
  { key: "adj_margin", label: "Adj Lighthouse Fee" },
  { key: "adj_margin_amount", label: "Adj Margin Amount" },
  { key: "adj_margin_two", label: "Adj Lighthouse Fee 2" },
  { key: "adj_margin_amount_two", label: "Adj Margin Amount 2" },
  { key: "adj_margin_three", label: "Adj Lighthouse Fee 3" },
  { key: "adj_margin_amount_three", label: "Adj Margin Amount 3" },
  { key: "expected_contract_futures_step_one", label: "Calculated Step 1 Futures" },
  { key: "expected_contract_futures_step_two", label: "Calculated Step 2 Futures" },
  { key: "expected_contract_futures", label: "Calculated Step 3 Futures" },
  { key: "expected_contract_futures_double_up", label: "Calculated DU Futures" },
  { key: "futures_margin_step_one", label: "Step 1 Futures Margin" },
  { key: "futures_margin_step_two", label: "Step 2 Futures Margin" },
  { key: "futures_margin", label: "Step 3 Futures Margin" },
  { key: "futures_margin_double_up", label: "DU Futures Margin" },
  { key: "final_futures_price_margin", label: "Final Futures Margin" },
];

/** Lookup map for derived field labels. */
const DERIVED_LABEL_MAP: Record<string, string> = Object.fromEntries(
  DERIVED_DISPLAY_FIELDS.map(({ key, label }) => [key, label]),
);

/**
 * Field keys that should never be displayed in the layer card.
 * These are internal IDs or references that are meaningless to users.
 */
const HIDDEN_FIELD_KEYS: ReadonlySet<string> = new Set([
  "child_contract_id",
  "split_customer_id",
  "trading_contract_id",
  "archived_contract_id",
  "roll_direction",
  "amendment_trader_id",
  "new_contract_type",
]);

/**
 * Display overrides for field keys that are not in the field registry but
 * still need proper labels and formatting in the patch section.
 */
const PATCH_DISPLAY_OVERRIDES: Record<string, OtcLayerFieldDef> = {
  split_quantity: { label: "Split Quantity", widget: "text" },
  source_contract_type: { label: "Source Contract Type", widget: "select", optionsSource: "contract_type" },
};

// ---------------------------------------------------------------------------
// Explicit row groupings for layer-1 general contract state.
// Each sub-array is rendered as its own FieldGrid row, matching the V2 form
// layout. Only fields with values in the snapshot are shown.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// User-entered fields per adjustment slot. Used to gate adj row visibility:
// if none of these have a value the row is hidden, even if derived margin
// fields default to 0.
// ---------------------------------------------------------------------------

const ADJ_USER_ENTERED_FIELDS: ReadonlyArray<ReadonlyArray<string>> = [
  ["adj_option_premium", "adj_contract_fee", "last_adjusted_date"],
  ["adj_option_premium_two", "adj_contract_fee_two", "adjusted_date_two"],
  ["adj_option_premium_three", "adj_contract_fee_three", "adjusted_date_three"],
];

const STATE_ROW_GROUPS: ReadonlyArray<ReadonlyArray<string>> = [
  // Row 1: date, end_date, contract_type, customer_id, commodity
  ["date", "end_date", "contract_type", "customer_id", "commodity"],
  // Row 2: option_month, quantity, trade_reference, account_no_a, has_invoice_fees, has_contract_adjustment
  [
    "option_month",
    "quantity",
    "trade_reference",
    "account_no_a",
    "has_invoice_fees",
    "has_contract_adjustment",
  ],
  // Steps row
  ["step_one", "step_two", "step_three", "double_up", "implied_fx_amount"],
  // Premium Offer row (gated by account_no_b having a value)
  ["account_no_b", "futures_price", "otc_futures"],
  // Accumulator row (gated by start_date having a value)
  ["start_date", "weeks", "period_qty", "knockout"],
  // Shipment / Trader
  [
    "shipment_start_date",
    "shipment_end_date",
    "trader_id",
    "crop_year",
    "rolled_option_month",
  ],
  // Carry / Basis / Notes
  ["carry", "rolled_basis_month", "basis_carry", "notes", "option_month_etc_one"],
];

function isEmptyValue(value: unknown): boolean {
  return (
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim() === "")
  );
}

function formatScalar(def: OtcLayerFieldDef, value: unknown): string | null {
  if (isEmptyValue(value)) return null;

  if (def.widget === "checkbox" || typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (def.widget === "date" && typeof value === "string") {
    const parsed = moment.tz(value, CommonDestinationTimezone);
    return parsed.isValid() ? parsed.format("MM/DD/YYYY") : value;
  }
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  return null;
}

/**
 * Resolves a display value for a registry field from a source object (snapshot
 * or field_patch), preferring a denormalized `display_snapshot` label when one
 * exists. Contract type / commodity fall back to their option label lookups.
 */
function resolveDisplayValue(
  field_name: string,
  def: OtcLayerFieldDef,
  source: Record<string, unknown>,
  display_snapshot: Record<string, unknown>,
): string | null {
  const labelKey = DISPLAY_SNAPSHOT_LABEL_KEYS[field_name];
  if (labelKey) {
    const label = display_snapshot[labelKey];
    if (typeof label === "string" && label.trim() !== "") {
      return label;
    }
  }

  const raw = source[field_name];
  if (field_name === "quantity" && typeof raw === 'string') {
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(
        safeParseDecimal({})(raw).toNumber(),
    )
  }
  if (field_name === "split_quantity" && typeof raw === 'string') {
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(
        safeParseDecimal({})(raw).toNumber(),
    )
  }
  if (field_name === "contract_reference" && typeof raw === "string") {
    return formatContractReferenceDisplay(raw);
  }
  if (field_name === "contract_type" && typeof raw === "string") {
    return findMatchOption(ContractTypeOptions, raw)?.label ?? raw;
  }
  if (field_name === "source_contract_type" && typeof raw === "string") {
    return findMatchOption(ContractTypeOptions, raw)?.label ?? raw;
  }
  if (field_name === "commodity" && typeof raw === "string") {
    return findMatchOption(CommodityOptions, raw)?.label ?? raw;
  }
  return formatScalar(def, raw);
}

/**
 * Returns true if at least one user-entered field in the given keys has a
 * non-empty value in the layer snapshot/display_snapshot. Derived fields
 * (margin, margin_amount) are intentionally excluded — they can default to 0.
 */
function hasAdjData(keys: readonly string[], layer: OtcContractLayerDto): boolean {
  return keys.some((key) => {
    const def = OTC_LAYER_FIELD_REGISTRY[key];
    if (!def) return false;
    const value = resolveDisplayValue(
      key,
      def,
      layer.snapshot,
      layer.display_snapshot,
    );
    return value !== null;
  });
}

/**
 * Derived fields that should be displayed with 4 decimal places.
 * These are futures-related calculated values.
 */
const FUTURES_DECIMAL_FIELDS = new Set<string>([
  "expected_contract_futures",
  "expected_contract_futures_step_one",
  "expected_contract_futures_step_two",
  "expected_contract_futures_double_up",
  "actual_contract_futures",
  "actual_contract_futures_step_one",
  "actual_contract_futures_step_two",
  "actual_contract_futures_double_up",
  "futures_margin",
  "futures_margin_step_one",
  "futures_margin_step_two",
  "futures_margin_double_up",
  "final_futures_price_margin",
]);

/**
 * Resolves a derived field value from the layer snapshots.
 * Futures-related values are rounded to 4 decimal places.
 */
function resolveDerivedValue(
  key: string,
  layer: OtcContractLayerDto,
): string | null {
  const raw = layer.display_snapshot[key] ?? layer.snapshot[key];
  if (isEmptyValue(raw)) return null;

  if (FUTURES_DECIMAL_FIELDS.has(key)) {
    const num = Number(raw);
    if (!Number.isNaN(num)) {
      return num.toFixed(4);
    }
  }

  return String(raw);
}

interface DisplayRow {
  key: string;
  label: string;
  value: string;
}

/**
 * Rounds a value to 4 decimal places if the field is a futures decimal field.
 * Returns the original string representation otherwise.
 */
function roundFuturesValue(key: string, raw: unknown): string {
  const str = String(raw);
  if (FUTURES_DECIMAL_FIELDS.has(key)) {
    const num = Number(raw);
    if (!Number.isNaN(num)) {
      return num.toFixed(4);
    }
  }
  return str;
}

/**
 * Builds DisplayRow entries for an ordered list of field keys. Each key is
 * tried first as a registry field (resolved via resolveDisplayValue), then
 * as a derived field (resolved from display_snapshot/snapshot). Keys with
 * no value or no definition are silently skipped.
 */
function buildFieldGridRows(
  keys: string[],
  layer: OtcContractLayerDto,
): DisplayRow[] {
  const rows: DisplayRow[] = [];
  for (const key of keys) {
    const def = OTC_LAYER_FIELD_REGISTRY[key];
    if (def) {
      const value = resolveDisplayValue(
        key,
        def,
        layer.snapshot,
        layer.display_snapshot,
      );
      if (value !== null) {
        rows.push({ key, label: def.label, value: roundFuturesValue(key, value) });
      }
      continue;
    }
    const derivedLabel = DERIVED_LABEL_MAP[key];
    if (derivedLabel) {
      const value = resolveDerivedValue(key, layer);
      if (value !== null) {
        rows.push({ key, label: derivedLabel, value });
      }
    }
  }
  return rows;
}

function FieldGrid({ rows }: { rows: DisplayRow[] }) {
  if (rows.length === 0) {
    return <div className="text-sm text-muted-foreground">No values.</div>;
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-2">
      {rows.map((row: DisplayRow) => (
        <div key={row.key} className="flex flex-col">
          <span className="text-xs text-muted-foreground">{row.label}</span>
          <span className="text-sm">{row.value}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Amendment section layout: grouped display of field_patch keys.
// Each amendment type defines an ordered list of sections. Each section has a
// label and an ordered list of field keys. Keys are consumed as they are
// rendered; any keys not consumed by any section fall into a trailing
// "Other Changes" fallback section.
// ---------------------------------------------------------------------------

interface AmendmentSectionDef {
  label: string;
  keys: string[];
}

/** Per-amendment-type section definitions (excluding adjustment + metadata). */
const AMENDMENT_SECTIONS: Record<string, ReadonlyArray<AmendmentSectionDef>> = {
  roll: [
    {
      label: "Roll",
      keys: [
        "rolled_option_month",
        "carry",
        "rolled_basis_month",
        "basis_carry",
      ],
    },
    {
      label: "Shipment",
      keys: [
        "shipment_start_date",
        "shipment_end_date",
        "crop_year",
      ],
    },
    {
      label: "Canola Futures",
      keys: [
        "implied_fx_amount",
        "actual_contract_futures_step_one",
        "actual_contract_futures_step_two",
        "actual_contract_futures",
        "actual_contract_futures_double_up",
        "final_futures_price",
        "final_futures_expected_price",
        "final_futures_price_date",
      ],
    },
  ],
  split: [
    {
      label: "Split",
      keys: ["split_quantity", "quantity", "contract_reference"],
    },
  ],
  exit: [
    {
      label: "Exit",
      keys: ["exit_date"],
    },
  ],
  contract_type_change: [
    {
      label: "New Contract Type",
      keys: ["contract_type", "source_contract_type"],
    },
    {
      label: "Steps",
      keys: ["step_one", "step_two", "step_three", "double_up"],
    },
    {
      label: "Premium Offer",
      keys: ["account_no_b", "futures_price", "otc_futures"],
    },
    {
      label: "Accumulator",
      keys: ["start_date", "weeks", "period_qty", "knockout"],
    },
    {
      label: "Canola Futures",
      keys: [
        "implied_fx_amount",
        "actual_contract_futures_step_one",
        "actual_contract_futures_step_two",
        "actual_contract_futures",
        "actual_contract_futures_double_up",
        "final_futures_price",
        "final_futures_expected_price",
        "final_futures_price_date",
      ],
    },
  ],
};

/** Adjustment slot field key sets, indexed by slot number (1-based).
 * Each slot lists ONLY the fields that belong to that specific slot. Rendered
 * from the layer snapshot (not field_patch), so the displayed values are the
 * fully-resolved adjustment values for that slot.
 */
const ADJ_SLOT_KEYS: ReadonlyArray<ReadonlyArray<string>> = [
  // Slot 1
  ["adj_option_premium", "adj_contract_fee", "adj_margin", "adj_margin_amount", "last_adjusted_date"],
  // Slot 2
  ["adj_option_premium_two", "adj_contract_fee_two", "adj_margin_two", "adj_margin_amount_two", "adjusted_date_two"],
  // Slot 3
  ["adj_option_premium_three", "adj_contract_fee_three", "adj_margin_three", "adj_margin_amount_three", "adjusted_date_three"],
];

/**
 * Adjustment-related keys that the backend may include in `field_patch` for
 * an amendment layer. These are silently consumed (added to the `consumed`
 * set) so they don't leak into "Other Changes". The adjustment section itself
 * renders values from the snapshot, not the patch.
 */
const ADJUSTMENT_PATCH_KEYS_TO_CONSUME: ReadonlySet<string> = new Set([
  // Backend's shared-payload base keys for the amendment's slot
  "option_premium", "contract_fee",
  // Both date variants for slot 1
  "adjusted_date", "last_adjusted_date",
  // Slot-specific adj keys (any slot)
  "adj_option_premium", "adj_contract_fee",
  "adj_option_premium_two", "adj_contract_fee_two", "adjusted_date_two",
  "adj_option_premium_three", "adj_contract_fee_three", "adjusted_date_three",
]);

/**
 * Metadata keys shown in a general section at the end. These appear for every
 * amendment type and need no special grouping.
 */
const AMENDMENT_METADATA_KEYS: ReadonlyArray<string> = [
  "notes",
  "has_invoice_fees",
  "has_contract_adjustment",
];

/**
 * Layer-level display_snapshot keys that should always be rendered for
 * amendment layers, even when they have no corresponding `field_patch` entry.
 * These are values the backend denormalizes onto the layer (e.g. the amendment
 * trader name) that live outside the normal field_patch flow.
 */
const AMENDMENT_DISPLAY_SNAPSHOT_KEYS: Record<string, string> = {
  amendment_trader_name: "Amendment Trader",
};

/**
 * Builds a DisplayRow from a `field_patch` entry, resolving labels/values
 * through the registry, overrides, or best-effort fallback.
 */
function buildPatchRow(
  key: string,
  field_patch: Record<string, unknown>,
  display_snapshot: Record<string, unknown>,
): DisplayRow | null {
  const def = getOtcLayerFieldDef(key) ?? PATCH_DISPLAY_OVERRIDES[key];
  if (def) {
    const value = resolveDisplayValue(key, def, field_patch, display_snapshot);
    if (value === null) return null;
    return { key, label: def.label, value };
  }
  const raw = field_patch[key];
  if (isEmptyValue(raw)) return null;
  if (typeof raw === "boolean") return { key, label: key, value: raw ? "Yes" : "No" };
  if (typeof raw === "string" || typeof raw === "number") return { key, label: key, value: String(raw) };
  return null;
}

interface AmendmentSection {
  label: string;
  rows: DisplayRow[];
}

/**
 * Builds ordered amendment sections from a layer's `field_patch`. Consumes
 * keys as they are placed into sections; any patch keys not covered by the
 * amendment-type layout, adjustment, or metadata sections are collected into
 * a trailing "Other Changes" section.
 */
function buildAmendmentSections(layer: OtcContractLayerDto): AmendmentSection[] {
  const patch = layer.field_patch;
  const display = layer.display_snapshot;
  const visibleKeys = Object.keys(patch).filter(
    (k) => !HIDDEN_FIELD_KEYS.has(k),
  );

  const consumed = new Set<string>();
  const sections: AmendmentSection[] = [];

  // 1. Amendment-type-specific sections
  const amendmentType = layer.amendment_type;
  const typeSections = amendmentType
    ? (AMENDMENT_SECTIONS[amendmentType] ?? [])
    : [];

  for (const section of typeSections) {
    const rows: DisplayRow[] = [];
    for (const key of section.keys) {
      if (!(key in patch)) continue;
      consumed.add(key);
      const row = buildPatchRow(key, patch, display);
      if (row) rows.push(row);
    }
    if (rows.length > 0) {
      sections.push({ label: section.label, rows });
    }
  }

  // 2. Adjustment slot section
  // Render from the layer snapshot for the assigned slot only. Patch keys
  // related to adjustments are silently consumed below so they don't fall
  // into "Other Changes".
  const slot = layer.adjustment_slot;
  if (typeof slot === "number" && slot >= 1 && slot <= 3) {
    // Only show the section if the user actually entered data for this slot.
    if (hasAdjData(ADJ_USER_ENTERED_FIELDS[slot - 1], layer)) {
      const adjRows = buildFieldGridRows([...ADJ_SLOT_KEYS[slot - 1]], layer);
      if (adjRows.length > 0) {
        sections.push({ label: `Adjustment ${slot}`, rows: adjRows });
      }
    }
    // Silently consume adjustment-related patch keys so they don't appear
    // in the "Other Changes" fallback section.
    for (const key of ADJUSTMENT_PATCH_KEYS_TO_CONSUME) {
      if (key in patch) consumed.add(key);
    }
  }

  // 3. General metadata section
  const metaRows: DisplayRow[] = [];
  for (const key of AMENDMENT_METADATA_KEYS) {
    if (!(key in patch)) continue;
    consumed.add(key);
    const row = buildPatchRow(key, patch, display);
    if (row) metaRows.push(row);
  }
  if (metaRows.length > 0) {
    sections.push({ label: "General", rows: metaRows });
  }

  // 3b. Layer-level display_snapshot values (e.g. amendment trader name).
  // These exist outside field_patch and are always rendered when present.
  const displaySnapshotRows: DisplayRow[] = [];
  for (const [displayKey, label] of Object.entries(AMENDMENT_DISPLAY_SNAPSHOT_KEYS)) {
    const value = display[displayKey];
    if (typeof value === "string" && value.trim() !== "") {
      displaySnapshotRows.push({ key: displayKey, label, value });
    }
  }
  if (displaySnapshotRows.length > 0) {
    sections.push({ label: "", rows: displaySnapshotRows });
  }

  // 4. Fallback: any remaining visible keys not consumed above
  const remainingKeys = visibleKeys.filter((k) => !consumed.has(k));
  if (remainingKeys.length > 0) {
    const fallbackRows: DisplayRow[] = [];
    for (const key of remainingKeys) {
      const row = buildPatchRow(key, patch, display);
      if (row) fallbackRows.push(row);
    }
    if (fallbackRows.length > 0) {
      sections.push({ label: "Other Changes", rows: fallbackRows });
    }
    fallbackRows.sort((a,b) => a.label.localeCompare(b.label))
  }

  return sections;
}

interface OtcContractLayerCardProps {
  layer: OtcContractLayerDto;
}

export function OtcContractLayerCard({ layer }: OtcContractLayerCardProps) {
  const lockedAt = layer.locked_at
    ? moment.tz(layer.locked_at, CommonDestinationTimezone)
    : null;
  const reviewedAt = layer.reviewed_at
    ? moment.tz(layer.reviewed_at, CommonDestinationTimezone)
    : null;

  const amendmentSections = layer.layer_no !== 1
    ? buildAmendmentSections(layer)
    : [];

  // Layer-1 state rows: each group is rendered as its own FieldGrid row.
  const stateRowGroups: DisplayRow[][] = STATE_ROW_GROUPS.map(
    (group: readonly string[]) => buildFieldGridRows([...group], layer),
  );

  // Economics row: option_premium, contract_fee, margin, margin_amount
  const economicsRow = buildFieldGridRows(
    ["option_premium", "contract_fee", "margin", "margin_amount"],
    layer,
  );

  // Adjustment rows: each slot rendered as its own 5-field row matching the
  // V2 form layout:
  //   adj_option_premium | adj_contract_fee | adj_margin | adj_margin_amount | adj_date
  const adjRow1 = buildFieldGridRows(
    [
      "adj_option_premium",
      "adj_contract_fee",
      "adj_margin",
      "adj_margin_amount",
      "last_adjusted_date",
    ],
    layer,
  );
  const adjRow2 = buildFieldGridRows(
    [
      "adj_option_premium_two",
      "adj_contract_fee_two",
      "adj_margin_two",
      "adj_margin_amount_two",
      "adjusted_date_two",
    ],
    layer,
  );
  const adjRow3 = buildFieldGridRows(
    [
      "adj_option_premium_three",
      "adj_contract_fee_three",
      "adj_margin_three",
      "adj_margin_amount_three",
      "adjusted_date_three",
    ],
    layer,
  );

  // Futures rows (canola only): per-step rows + final row, matching V2 form.
  // Order: Step 1, Step 2, Step 3, Double Up.
  // Each row: Calculated | Quoted | Margin.
  const futuresStepSuffixes = ["_step_one", "_step_two", "", "_double_up"];
  const futuresStepRows: DisplayRow[][] = futuresStepSuffixes.map(
    (suffix: string) =>
      buildFieldGridRows(
        [
          `expected_contract_futures${suffix}`,
          `actual_contract_futures${suffix}`,
          `futures_margin${suffix}`,
        ],
        layer,
      ),
  );
  const futuresFinalRow = buildFieldGridRows(
    [
      "final_futures_price_margin",
    ],
    layer,
  );

  return (
    <div className="rounded-lg border p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium">
            Layer #{layer.layer_no} — {generateAmendmentTitleForLayer(layer)}
          </span>
          {typeof layer.adjustment_slot === "number" && (
            <Badge variant="outline">Adj slot {layer.adjustment_slot}</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {lockedAt && lockedAt.isValid() && (
            <span className="text-xs text-muted-foreground">
              Locked {lockedAt.format("MM/DD/YYYY")}
            </span>
          )}
          {layer.staged_for_review && (
            <Badge variant="outline">Pending review</Badge>
          )}
          {!layer.staged_for_review && reviewedAt && reviewedAt.isValid() && (
            <span className="text-xs text-muted-foreground">
              Reviewed {reviewedAt.format("MM/DD/YYYY")}
            </span>
          )}
          <Badge variant={layer.status === "current" ? "default" : "secondary"}>
            {layer.status}
          </Badge>
        </div>
      </div>

      {layer.layer_no !== 1 && amendmentSections.length > 0 && (
        <>
          {amendmentSections.map(
            (section: AmendmentSection, idx: number) => (
              <div key={idx} className="flex flex-col gap-1">
                <span className="text-xs font-semibold uppercase text-muted-foreground">
                  {section.label}
                </span>
                <FieldGrid rows={section.rows} />
              </div>
            ),
          )}
        </>
      )}

      {layer.layer_no === 1 && (
        <>
          {/* General contract state — each row group rendered separately */}
          {stateRowGroups.map(
            (group: DisplayRow[], idx: number) =>
              group.length > 0 && (
                <div key={idx} className="flex flex-col gap-1">
                  <FieldGrid rows={group} />
                </div>
              ),
          )}

          {/* Economics: premium, fee, margin, margin amount */}
          {economicsRow.length > 0 && (
            <div className="flex flex-col gap-1">
              <FieldGrid rows={economicsRow} />
            </div>
          )}

          {/* Adjustment 1 */}
          {adjRow1.length > 0 && hasAdjData(ADJ_USER_ENTERED_FIELDS[0], layer) && (
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold uppercase text-muted-foreground">
                Adjustment 1
              </span>
              <FieldGrid rows={adjRow1} />
            </div>
          )}

          {/* Adjustment 2 */}
          {adjRow2.length > 0 && hasAdjData(ADJ_USER_ENTERED_FIELDS[1], layer) && (
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold uppercase text-muted-foreground">
                Adjustment 2
              </span>
              <FieldGrid rows={adjRow2} />
            </div>
          )}

          {/* Adjustment 3 */}
          {adjRow3.length > 0 && hasAdjData(ADJ_USER_ENTERED_FIELDS[2], layer) && (
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold uppercase text-muted-foreground">
                Adjustment 3
              </span>
              <FieldGrid rows={adjRow3} />
            </div>
          )}

          {/* Futures-derived (canola only) — per-step rows + final row */}
          {(futuresStepRows.some((r: DisplayRow[]) => r.length > 0) ||
            futuresFinalRow.length > 0) && (
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold uppercase text-muted-foreground">
                Futures Margins
              </span>
              {futuresStepRows.map(
                (stepRow: DisplayRow[], idx: number) =>
                  stepRow.length > 0 && (
                    <FieldGrid key={idx} rows={stepRow} />
                  ),
              )}
              {futuresFinalRow.length > 0 && (
                <FieldGrid rows={futuresFinalRow} />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
